import React, { useState } from "react";
import axios from "axios";
import { Translate, ArrowsLeftRight, Copy, ChatText, EnvelopeSimple } from "@phosphor-icons/react";
import { Textarea } from "../components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function TranslatorPage() {
  const [inputText, setInputText] = useState("");
  const [outputText, setOutputText] = useState("");
  const [sourceLang, setSourceLang] = useState("en");
  const [targetLang, setTargetLang] = useState("es");
  const [context, setContext] = useState("sms");
  const [translating, setTranslating] = useState(false);
  const [history, setHistory] = useState([]);

  const handleTranslate = async () => {
    if (!inputText.trim()) { toast.error("Type something to translate"); return; }
    setTranslating(true);
    try {
      const res = await axios.post(`${API}/translate`, {
        text: inputText.trim(),
        source_lang: sourceLang,
        target_lang: targetLang,
        context,
      });
      setOutputText(res.data.translated);
      setHistory(prev => [{
        original: res.data.original,
        translated: res.data.translated,
        from: sourceLang,
        to: targetLang,
        context,
        time: new Date().toLocaleTimeString(),
      }, ...prev].slice(0, 20));
    } catch (e) {
      toast.error("Translation failed. Try again.");
    } finally {
      setTranslating(false);
    }
  };

  const swapLanguages = () => {
    setSourceLang(targetLang);
    setTargetLang(sourceLang);
    setInputText(outputText);
    setOutputText(inputText);
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    toast.success("Copied to clipboard");
  };

  const langLabel = (code) => code === "en" ? "English" : "Español";

  return (
    <div data-testid="translator-page" className="space-y-6">
      <div>
        <h1
          className="text-3xl sm:text-4xl font-black tracking-tighter"
          style={{ fontFamily: "Cabinet Grotesk, sans-serif" }}
        >
          Translator
        </h1>
        <p className="text-sm text-[#52525B] mt-1">
          Type in English, send in Spanish. Or vice versa. AI-powered natural translations for SMS and email.
        </p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {/* Input Panel */}
        <div className="bg-white border border-border">
          <div className="flex items-center justify-between px-4 py-3 border-b border-border bg-[#FAFAFA]">
            <div className="flex items-center gap-2">
              <span className={`px-2.5 py-1 text-xs font-bold ${sourceLang === "en" ? "bg-[#002FA7] text-white" : "bg-[#FF9100] text-white"}`}>
                {langLabel(sourceLang)}
              </span>
            </div>
            <div className="flex items-center gap-2">
              <Select value={context} onValueChange={setContext}>
                <SelectTrigger data-testid="context-select" className="h-7 w-[100px] text-xs border-border rounded-none">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="sms"><ChatText weight="bold" size={12} className="inline mr-1" />SMS</SelectItem>
                  <SelectItem value="email"><EnvelopeSimple weight="bold" size={12} className="inline mr-1" />Email</SelectItem>
                  <SelectItem value="general">General</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <Textarea
            data-testid="translate-input"
            value={inputText}
            onChange={(e) => setInputText(e.target.value)}
            placeholder={sourceLang === "en"
              ? "Type your message in English..."
              : "Escribe tu mensaje en Español..."
            }
            rows={8}
            className="border-none rounded-none focus:ring-0 resize-none text-base px-4 py-3"
            onKeyDown={(e) => { if (e.key === "Enter" && e.ctrlKey) handleTranslate(); }}
          />
          <div className="flex items-center gap-2 px-4 py-3 border-t border-border">
            <button
              data-testid="translate-btn"
              onClick={handleTranslate}
              disabled={translating}
              className="flex-1 flex items-center justify-center gap-2 px-4 py-2.5 bg-[#002FA7] text-white text-sm font-semibold hover:bg-[#002482] transition-colors disabled:opacity-50"
            >
              {translating ? (
                <><div className="spinner" style={{ width: 14, height: 14, borderTopColor: "white", borderColor: "rgba(255,255,255,0.3)" }} /> Translating...</>
              ) : (
                <><Translate weight="bold" size={16} /> Translate</>
              )}
            </button>
            <button
              data-testid="swap-langs"
              onClick={swapLanguages}
              className="p-2.5 border border-border hover:border-[#002FA7] hover:text-[#002FA7] transition-colors"
              title="Swap languages"
            >
              <ArrowsLeftRight weight="bold" size={16} />
            </button>
          </div>
        </div>

        {/* Output Panel */}
        <div className="bg-white border border-border">
          <div className="flex items-center justify-between px-4 py-3 border-b border-border bg-[#FAFAFA]">
            <span className={`px-2.5 py-1 text-xs font-bold ${targetLang === "es" ? "bg-[#FF9100] text-white" : "bg-[#002FA7] text-white"}`}>
              {langLabel(targetLang)}
            </span>
            {outputText && (
              <button
                data-testid="copy-translation"
                onClick={() => copyToClipboard(outputText)}
                className="flex items-center gap-1 text-xs font-semibold text-[#002FA7] hover:underline"
              >
                <Copy weight="bold" size={12} />
                Copy
              </button>
            )}
          </div>
          <div className="min-h-[200px] px-4 py-3">
            {outputText ? (
              <p className="text-base leading-relaxed whitespace-pre-wrap">{outputText}</p>
            ) : (
              <p className="text-[#A1A1AA] text-sm">
                {translating ? "Translating..." : "Translation will appear here"}
              </p>
            )}
          </div>
          {outputText && (
            <div className="flex gap-2 px-4 py-3 border-t border-border">
              <button
                data-testid="copy-for-sms"
                onClick={() => {
                  copyToClipboard(outputText);
                  toast.success("Copied! Paste into your SMS app");
                }}
                className="flex-1 flex items-center justify-center gap-1 px-3 py-2 border border-border text-xs font-semibold hover:border-[#002FA7] hover:text-[#002FA7] transition-colors"
              >
                <ChatText weight="bold" size={13} />
                Copy for SMS
              </button>
              <button
                data-testid="copy-for-email"
                onClick={() => {
                  copyToClipboard(outputText);
                  toast.success("Copied! Paste into your email");
                }}
                className="flex-1 flex items-center justify-center gap-1 px-3 py-2 border border-border text-xs font-semibold hover:border-[#002FA7] hover:text-[#002FA7] transition-colors"
              >
                <EnvelopeSimple weight="bold" size={13} />
                Copy for Email
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Translation History */}
      {history.length > 0 && (
        <div className="bg-white border border-border">
          <div className="px-4 py-3 border-b border-border">
            <h3 className="text-sm font-bold tracking-tight" style={{ fontFamily: "Cabinet Grotesk, sans-serif" }}>
              Recent Translations
            </h3>
          </div>
          <div className="divide-y divide-border max-h-[300px] overflow-y-auto">
            {history.map((h, i) => (
              <div key={i} className="px-4 py-3 hover:bg-[#F5F5F7] transition-colors cursor-pointer" onClick={() => {
                setInputText(h.original);
                setOutputText(h.translated);
                setSourceLang(h.from);
                setTargetLang(h.to);
              }}>
                <div className="flex items-center gap-2 mb-1">
                  <span className={`px-1.5 py-0.5 text-[10px] font-bold ${h.from === "en" ? "bg-[#002FA7] text-white" : "bg-[#FF9100] text-white"}`}>
                    {h.from.toUpperCase()}
                  </span>
                  <span className="text-[10px] text-[#A1A1AA]">→</span>
                  <span className={`px-1.5 py-0.5 text-[10px] font-bold ${h.to === "es" ? "bg-[#FF9100] text-white" : "bg-[#002FA7] text-white"}`}>
                    {h.to.toUpperCase()}
                  </span>
                  <span className="text-[10px] text-[#A1A1AA] ml-auto">{h.context} &middot; {h.time}</span>
                </div>
                <p className="text-xs text-[#52525B] truncate">{h.original}</p>
                <p className="text-xs font-medium truncate">{h.translated}</p>
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="bg-[#FAFAFA] border border-border p-4">
        <p className="text-xs text-[#52525B]">
          <strong>Tip:</strong> Press <kbd className="px-1.5 py-0.5 bg-white border border-border text-[10px] font-mono">Ctrl+Enter</kbd> to translate quickly. 
          Translations use natural, professional Mexican/American Spanish — not Google Translate.
        </p>
      </div>
    </div>
  );
}
